import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Phone, X, ChevronLeft, ChevronRight, Expand, Check } from "lucide-react";
import api from "../api";
import { useLang, Link } from "../i18n";
import VehicleCard from "../components/VehicleCard";
import EnquiryForm from "../components/EnquiryForm";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { Helmet } from "react-helmet-async";
import ShareButtons from "../components/ShareButtons";

const STATUS_STYLE = {
  available: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  reserved: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  sold: "bg-red-500/15 text-red-400 border-red-500/30",
};

function FinanceCalculator({ price }) {
  const { t } = useLang();
  const [deposit, setDeposit] = useState(20);
  const [term, setTerm] = useState(36);

  const monthly = useMemo(() => {
    const principal = price * (1 - deposit / 100);
    const total = principal * (1 + 0.085 * (term / 12));
    return Math.round(total / term);
  }, [price, deposit, term]);

  return (
    <div data-testid="finance-calculator" className="bg-[#12141D] border border-white/10 rounded-3xl p-8 spotlight-card overflow-hidden">
      <h3 className="font-display text-xl font-semibold text-slate-50">{t("det.finance")}</h3>
      <div className="mt-8 space-y-8">
        <div>
          <div className="flex justify-between text-sm mb-3">
            <span className="text-slate-400">{t("det.deposit")}</span>
            <span className="font-mono-num text-[#4499F7]">{deposit}%</span>
          </div>
          <input
            data-testid="finance-deposit-slider"
            type="range"
            min="10"
            max="50"
            step="5"
            value={deposit}
            onChange={(e) => setDeposit(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <div className="flex justify-between text-sm mb-3">
            <span className="text-slate-400">{t("det.term")}</span>
            <span className="font-mono-num text-[#4499F7]">{term} {t("det.months")}</span>
          </div>
          <input
            data-testid="finance-term-slider"
            type="range"
            min="12"
            max="60"
            step="6"
            value={term}
            onChange={(e) => setTerm(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <div className="pt-6 border-t border-white/10 flex items-end justify-between">
          <span className="text-sm text-slate-400">{t("det.monthly")}</span>
          <span data-testid="finance-monthly-value" className="font-mono-num text-3xl font-bold text-[#4499F7]">
            €{monthly.toLocaleString("en-US").replace(/,/g, " ")}
          </span>
        </div>
      </div>
    </div>
  );
}

function Gallery({ images, title, statusBadge }) {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  useEffect(() => {
    setActive(0);
    setLightbox(false);
  }, [images]);

  const step = (dir) => setActive((a) => (a + dir + images.length) % images.length);

  return (
    <div>
      <div
        className="relative rounded-3xl overflow-hidden border border-white/10 spotlight-card cursor-zoom-in group"
        data-testid="vehicle-gallery"
        onClick={() => setLightbox(true)}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={active}
            src={imgSrc(images[active])}
            alt={title}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full aspect-[16/10] object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10]/70 via-transparent to-transparent pointer-events-none" />
        {statusBadge}
        <span className="absolute top-5 right-5 flex items-center gap-2 text-[11px] font-mono-num tracking-widest uppercase text-slate-200 bg-black/50 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Expand size={12} />
          {active + 1} / {images.length} {t("gallery.photos")}
        </span>
      </div>

      {images.length > 1 && (
        <div className="mt-4 grid grid-cols-4 gap-3" data-testid="vehicle-thumbnails">
          {images.map((img, i) => (
            <button
              key={i}
              data-testid={`vehicle-thumb-${i}`}
              onClick={() => setActive(i)}
              className={`relative aspect-[16/10] rounded-xl overflow-hidden border transition-colors ${
                i === active ? "border-[#4499F7]" : "border-white/10 hover:border-white/30"
              }`}
            >
              <img src={imgSrc(img)} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="vehicle-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={() => setLightbox(false)}
          >
            <button
              data-testid="lightbox-close-btn"
              onClick={() => setLightbox(false)}
              className="absolute top-6 right-6 text-slate-300 hover:text-[#4499F7] transition-colors p-2"
              aria-label="Close"
            >
              <X size={28} />
            </button>
            {images.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev-btn"
                  onClick={(e) => { e.stopPropagation(); step(-1); }}
                  className="absolute left-4 sm:left-8 text-slate-300 hover:text-[#4499F7] transition-colors p-3 border border-white/10 rounded-full bg-black/40"
                  aria-label="Previous"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  data-testid="lightbox-next-btn"
                  onClick={(e) => { e.stopPropagation(); step(1); }}
                  className="absolute right-4 sm:right-8 text-slate-300 hover:text-[#4499F7] transition-colors p-3 border border-white/10 rounded-full bg-black/40"
                  aria-label="Next"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            <motion.img
              key={active}
              src={imgSrc(images[active])}
              alt={title}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono-num text-xs tracking-widest text-slate-400">
              {active + 1} / {images.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const [vehicle, setVehicle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setVehicle(null);
    setNotFound(false);
    api.get(`/vehicles/${id}`)
      .then((r) => setVehicle(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!vehicle) return;
    api.get("/vehicles", { params: { category: vehicle.category } })
      .then((r) => setRelated(r.data.filter((v) => v.id !== vehicle.id).slice(0, 3)))
      .catch(() => {});
  }, [vehicle]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="vehicle-not-found">
        <p className="text-slate-400">404</p>
        <Link to="/inventory" data-testid="vehicle-back-link" className="mt-4 inline-block text-[#4499F7] font-semibold">
          {t("common.back")}
        </Link>
      </div>
    );
  }

  if (!vehicle) {
    return <div className="pt-40 pb-32 text-center text-slate-500" data-testid="vehicle-loading">{t("common.loading")}</div>;
  }

  const v = vehicle;
  const images = Array.isArray(v.images) && v.images.length > 0 ? v.images : [v.image];
  const specs = [
    { label: t("det.year"), value: v.year || "" },
    { label: t("det.mileage"), value: v.mileage > 0 ? `${v.mileage.toLocaleString("en-US").replace(/,/g, " ")} ${t("det.km")}` : "" },
    { label: t("det.gearbox"), value: v.gearbox },
    { label: t("det.drivetrain"), value: v.drivetrain },
    { label: t("det.fuel"), value: v.fuel },
    { label: t("det.engine"), value: v.engine },
    { label: t("det.power"), value: v.power },
    { label: t("det.emission"), value: v.emission },
    { label: t("det.color"), value: v.color },
    { label: t("det.category"), value: t(`cats.${v.category}`) },
  ].filter((s) => s.value !== "" && s.value != null && s.value !== "N/A");

  const statusBadge = (
    <>
      <span
        data-testid="vehicle-status-badge"
        className={`absolute top-5 left-5 text-[11px] font-mono-num tracking-widest uppercase border rounded-full px-3 py-1 backdrop-blur-md ${STATUS_STYLE[v.status] || STATUS_STYLE.available}`}
      >
        {t(`status.${v.status}`)}
      </span>
      {v.offer && (
        <span
          data-testid="vehicle-offer-ribbon"
          className="absolute top-8 -right-10 rotate-45 bg-amber-400 text-[#0A0B10] text-[10px] font-mono-num font-bold tracking-[0.2em] uppercase px-12 py-1.5 shadow-lg"
        >
          {t("offer.badge")}
        </span>
      )}
    </>
  );

  return (
    <div data-testid="vehicle-detail-page" className="pt-32 pb-24">
      <Seo
        title={`${v.title} — KATACO`}
        description={`${v.title}${v.year ? ` (${v.year})` : ""} — ${t("seo.vehDescSuffix")}`}
        image={v.image}
        type="product"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": v.category === "cars" ? "Car" : "Product",
            name: v.title,
            image: images.filter((u) => u.startsWith("https://")),
            brand: { "@type": "Brand", name: v.brand },
            ...(v.year ? { vehicleModelDate: String(v.year) } : {}),
            ...(v.mileage > 0 ? { mileageFromOdometer: { "@type": "QuantitativeValue", value: v.mileage, unitCode: "KMT" } } : {}),
            ...(v.fuel ? { fuelType: v.fuel } : {}),
            offers: {
              "@type": "Offer",
              price: v.price,
              priceCurrency: "EUR",
              availability: v.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
              itemCondition: "https://schema.org/UsedCondition",
              seller: { "@type": "AutoDealer", name: "KATACO", telephone: "+40723250260" },
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: t("nav.home") },
              { "@type": "ListItem", position: 2, name: t("nav.inventory") },
              { "@type": "ListItem", position: 3, name: v.title },
            ],
          })}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/inventory"
          data-testid="vehicle-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#4499F7] transition-colors"
        >
          <ArrowLeft size={16} />
          {t("common.back")}
        </Link>

        <div className="mt-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <Gallery images={images} title={v.title} statusBadge={statusBadge} />
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10" data-testid="vehicle-specs">
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-50">{t("det.specs")}</h2>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
                  {specs.map((s) => (
                    <div key={s.label} className="bg-[#12141D] p-5">
                      <p className="text-[11px] font-mono-num tracking-widest uppercase text-slate-500">{s.label}</p>
                      <p className="mt-2 font-mono-num text-sm font-semibold text-slate-100">{s.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {v.description && (
              <Reveal delay={0.15}>
                <div className="mt-10" data-testid="vehicle-features">
                  <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-50">{t("det.features")}</h2>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {v.description.split("\n").filter(Boolean).map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-slate-300">
                        <Check size={15} className="text-[#4499F7] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5 space-y-8">
            <Reveal delay={0.05}>
              <div>
                <p className="font-mono-num text-xs tracking-[0.3em] uppercase text-[#4499F7]">{v.brand}</p>
                <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-50" data-testid="vehicle-title">
                  {v.title}
                </h1>
                <p className="mt-5 font-mono-num text-4xl font-bold text-[#4499F7]" data-testid="vehicle-price">
                  €{v.price.toLocaleString("en-US").replace(/,/g, " ")}
                </p>
                {v.offer && (
                  <span
                    data-testid="vehicle-offer-badge"
                    className="mt-4 inline-block bg-amber-400 text-[#0A0B10] text-xs font-mono-num font-bold tracking-[0.2em] uppercase rounded-full px-4 py-1.5"
                  >
                    {t("offer.badge")}
                  </span>
                )}
                <a
                  href="tel:+40723250260"
                  data-testid="vehicle-call-btn"
                  className="mt-7 inline-flex items-center gap-3 bg-[#4499F7] text-[#0A0B10] font-semibold rounded-full px-7 py-3.5 hover:bg-[#6FB1FA] transition-colors"
                >
                  <Phone size={16} />
                  {t("det.call")} · +40 723 250 260
                </a>
                <ShareButtons title={v.title} />
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <FinanceCalculator price={v.price} />
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-[#12141D] border border-white/10 rounded-3xl p-8">
                <h3 className="font-display text-xl font-semibold text-slate-50 mb-6">{t("det.enquireTitle")}</h3>
                <EnquiryForm vehicle={v} testPrefix="vehicle" />
              </div>
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24" data-testid="related-vehicles">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">{t("det.related")}</h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {related.map((r, i) => (
                <VehicleCard key={r.id} vehicle={r} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
